// ============================================================
// Sample Property Management portfolio for the showcase (demoMode).
//
// Without this, the Property Mgmt tab shows "No Properties Yet" in demo mode
// (its /pm/owner/:id/units call is blocked), which reads as a feature that
// does not work rather than a deliberate sample.
//
// Shape follows what PropertyManagementView reads off each unit: the
// dashboard totals income from `rentSchedule` and `expenses`, the tenant card
// from `currentTenant`, and the per-property sub-tabs from `inspectionReports`,
// `maintenance`, `tenancyHistory` and `lifecycle`.
//
// Seagate and Damac Bay are written out by hand; the rest of the held
// portfolio is generated at the foot of this file, so this tab shows the same
// ten properties the portfolio does. The off-plan units are deliberately NOT
// under management — they are not built yet, which is exactly why a real
// portfolio has some units here and some not. That contrast is worth showing.
//
// DEMO_LEASES at the foot of this file is wider than DEMO_PM_UNITS on
// purpose: every tenanted unit has a lease (so the portfolio report's rent
// roll is complete), while full management fixtures — tenants, inspections,
// maintenance, lifecycle — exist for Seagate alone.
//
// Names, cheque numbers and DEWA/plot references are fabricated.
// ============================================================

import { TENANCY_CHEQUES } from '../shared/mockData';
import { DEMO_PORTAL_UNITS } from './demoAssetDeskData';

/** Fixed dates so the sample stays coherent, near the Aug 2026 snapshot. */
const CONTRACT_START = '2026-01-15';
const CONTRACT_END = '2027-01-14';

export const DEMO_PM_UNITS: any[] = [
  {
    id: 'demo-unit-seagate',
    name: 'Seagate Building 2',
    unitNumber: '1204',
    community: 'Mina Rashid',
    location: 'Mina Rashid, Dubai',
    propertyType: 'Apartment',
    bedrooms: 2,
    sizeSqft: 1412,
    areaSqm: 131.2,
    purchasePrice: 3150000,
    // EXACTLY 'Rented': PropertyManagementView counts occupancy with
    // u.status === 'Rented' (capital R, strict equality), and PropertyView's
    // tab filter lowercases it. 'leased' satisfied neither and showed 0 of 2.
    status: 'Rented',
    usage: 'Residential',
    propertyNo: 'P-1204-SG2',
    plotNo: '3410221',
    // The numbers Dubai's Rental Index form asks for. Without these the
    // Property Details block on a RENTED unit renders as a row of gaps.
    dewaPremisesNo: '321039726',
    makaniNo: '2688494205',
    image: '',
    projectImage: '',

    currentTenant: {
      name: 'Mounir Haddad',
      // What an owner needs to reach their tenant, and the ID the contract is
      // registered against. Fabricated, in the real formats: an Emirates ID is
      // 784-<birth year>-<7 digits>-<check digit>.
      emiratesId: '784-1986-4417029-3',
      email: 'm.haddad@example.com',
      phone: '+971 50 447 1182',
      annualRent: 198000,
      contractStart: CONTRACT_START,
      contractEnd: CONTRACT_END,
      ejariNo: 'EJ-2026-118427',
      occupants: 3,
    },

    // The same four cheques the tenant sees, off the same calendar — a cheque
    // whose date has passed has been banked, so the year is part-collected
    // rather than suspiciously perfect, and it stays that way as time moves.
    rentSchedule: TENANCY_CHEQUES.map((c) => ({
      chequeNo: c.chequeNo,
      amount: c.amount,
      dueDate: c.iso,
      status: c.status,
    })),

    expenses: [
      { id: 'exp-1', category: 'Service charge', description: 'Q1 service charge', amount: 8470, date: '2026-02-02' },
      { id: 'exp-2', category: 'Service charge', description: 'Q2 service charge', amount: 8470, date: '2026-05-04' },
      { id: 'exp-3', category: 'Maintenance', description: 'AC service — annual', amount: 1200, date: '2026-03-18' },
      { id: 'exp-4', category: 'Management fee', description: 'Management fee (5% of collected rent)', amount: 4950, date: '2026-04-20' },
    ],

    maintenance: [
      { id: 'mt-1', title: 'Kitchen mixer tap dripping', category: 'Plumbing', status: 'completed', createdAt: '2026-03-04', completedAt: '2026-03-06', cost: 320 },
      { id: 'mt-2', title: 'Bedroom AC not cooling', category: 'AC / Cooling', status: 'in_progress', createdAt: '2026-07-28', cost: 0 },
      // One still OPEN, and raised by the tenant rather than by the management
      // team — so the maintenance board demonstrates all three states, and the
      // showcase shows the thing that is actually new about it: a request the
      // tenant reported arriving in front of the owner on its own.
      { id: 'mt-3', title: 'Bathroom extractor fan noisy', category: 'Electrical', status: 'open', createdAt: '2026-09-12', cost: 0, source: 'tenant' },
    ],

    inspectionReports: [
      { id: 'ins-1', type: 'Move-in inspection', date: CONTRACT_START, inspector: 'Property team', findings: 'Unit handed over in good condition. Inventory list signed by tenant.', status: 'completed' },
      { id: 'ins-2', type: 'Mid-term inspection', date: '2026-07-10', inspector: 'Property team', findings: 'No structural issues. AC filters replaced. Balcony sealant to be reviewed at renewal.', status: 'completed' },
    ],

    tenancyHistory: [
      { tenant: 'Mounir Haddad', from: CONTRACT_START, to: CONTRACT_END, annualRent: 198000, status: 'current' },
      { tenant: 'R. Okafor', from: '2024-12-01', to: '2025-11-30', annualRent: 178000, status: 'ended' },
    ],

    lifecycle: [
      { step: 'Onboarded', date: '2025-12-20', completed: true },
      { step: 'Marketed', date: '2025-12-28', completed: true },
      { step: 'Tenant found', date: '2026-01-08', completed: true },
      { step: 'Ejari registered', date: '2026-01-16', completed: true },
      { step: 'Renewal due', date: '2026-11-15', completed: false },
    ],

    // `facts` mirrors what pmRoutes.buildUnitFacts computes for a live unit:
    // the resolved service charge, DLD construction state, expected rent for
    // units with none of their own, and the two owner-declared facts. Present
    // here so the showcase shows the same card a real owner sees.
    facts: {
      serviceCharge: { psf: 14.5, annual: 20474, source: 'invoice', sqft: 1412, bookedAsExpense: true, payableYet: true },
      construction: { offplan: false, percent: 100, percentSource: 'dld_project_registry', status: 'finished', handover: '2024-06-30', handoverSource: 'unit', developer: 'Emaar' },
      expectedRent: null,
      mortgage: { status: 'yes', lender: 'Emirates NBD', outstanding: 1420000, monthlyPayment: 9800, updatedAt: '2026-08-02' },
      letting: { mode: 'long_term', brand: null },
      photo: null,
      // Rent 198,000 − expenses 23,090 (the service-charge invoice is already
      // one of those rows, hence serviceCharge: 0 — never deducted twice).
      netIncome: { annualRent: 198000, expenses: 23090, serviceCharge: 0, net: 174910, serviceChargeKnown: true },
    },
    documents: [
      { id: 'pmdoc-1', name: 'Tenancy contract 2026.pdf', type: 'Tenancy Contract', date: CONTRACT_START },
      { id: 'pmdoc-2', name: 'Ejari certificate.pdf', type: 'Ejari Certificate', date: '2026-01-16' },
      { id: 'pmdoc-3', name: 'Inventory list — signed.pdf', type: 'Inventory List', date: CONTRACT_START },
      { id: 'pmdoc-4', name: 'Move-in inspection report.pdf', type: 'Handover Document', date: CONTRACT_START },
    ],
  },

  {
    id: 'demo-unit-damac-bay',
    name: 'Damac Bay 2 · Tower B',
    unitNumber: '2408',
    community: 'Dubai Harbour',
    location: 'Dubai Harbour, Dubai',
    propertyType: 'Apartment',
    bedrooms: 1,
    sizeSqft: 782,
    areaSqm: 72.6,
    purchasePrice: 2050000,
    // Under construction: no tenant, no rent, no expenses yet. A real
    // portfolio looks like this, and the empty sections are honest.
    status: 'offplan',
    usage: 'Residential',
    propertyNo: 'P-2408-DB2',
    plotNo: '—',
    dewaPremisesNo: '—',
    image: '',
    projectImage: '',

    currentTenant: null,
    rentSchedule: [],
    expenses: [],
    maintenance: [],
    inspectionReports: [],
    tenancyHistory: [],
    lifecycle: [
      { step: 'Purchased', date: '2023-05-08', completed: true },
      { step: 'Under construction', date: '2023-11-15', completed: true },
      { step: 'Handover expected', date: '2027-09-30', completed: false },
      { step: 'Onboard to management', date: '', completed: false },
    ],
    facts: {
      // Rate known, not payable yet: Mollak bills from handover, so it is
      // shown as what the unit will cost and left out of the net.
      serviceCharge: { psf: 18.2, annual: 14232, source: 'dld_index', sqft: 782, bookedAsExpense: false, payableYet: false },
      construction: { offplan: true, percent: 64.8, percentSource: 'dld_project_registry', status: 'active', handover: '2027-09-30', handoverSource: 'unit', developer: 'DAMAC Properties', lastSync: '2026-08-30' },
      expectedRent: { available: true, annual: 132000, monthly: 11000, psf: 168.8, sampleSize: 41, confidence: 'medium' },
      mortgage: { status: 'no', lender: null, outstanding: null, monthlyPayment: null, updatedAt: '2026-08-02' },
      letting: { mode: 'short_term', brand: 'DreamHaven' },
      photo: null,
      netIncome: { annualRent: 0, expenses: 0, serviceCharge: 0, net: 0, serviceChargeKnown: true },
    },
    documents: [
      { id: 'pmdoc-5', name: 'Sale & purchase agreement.pdf', type: 'SPA', date: '2023-05-08' },
      { id: 'pmdoc-6', name: 'Payment plan.pdf', type: 'Payment Plan', date: '2023-05-08' },
    ],
  },
];

/**
 * Signed leases, keyed by unit id — the `LeaseInfo` map useLeases normally
 * builds from /pm/owner/:id/units.
 *
 * WHY THIS IS SEPARATE, and why the report needs it: buildRows takes rental
 * income ONLY from a signed Property Management lease
 * (`annualRent = lease?.annualRent || 0`) and never from a market estimate,
 * deliberately — so "rented" reflects actual tenancies rather than a guess.
 * With the PM call blocked in demo mode there were no leases, so the report
 * showed "Annual net rent €0 · No rented units yet" beside a unit clearly
 * marked Occupied.
 *
 * Figures must agree with the Seagate rentSchedule above: annual rent is the
 * four cheques (4 × 49,500 = 198,000), collected is the ones actually banked
 * (derived from TENANCY_CHEQUES, never typed), and expenses is the sum of the
 * expense rows.
 */
export const DEMO_LEASES: Record<string, { annualRent: number; expenses: number; collected: number; tenant?: string; status?: string }> = {
  'demo-unit-seagate': {
    annualRent: 198000,
    expenses: 23090,   // 8,470 + 8,470 + 1,200 + 4,950
    // The cheques banked so far — off the shared calendar, so this can never
    // contradict the rent schedule above or the tenant's own view.
    collected: TENANCY_CHEQUES.filter((c) => c.paid).reduce((sum, c) => sum + c.amount, 0),
    tenant: 'Mounir Haddad',
    status: 'Rented',
  },
  // The other four tenanted units. Each annual rent is twelve times the
  // monthly rent on the unit in demoAssetDeskData, so the portfolio report's
  // rent roll and the Asset Desk cards cannot disagree. Collected is the part
  // of the year banked so far — two cheques of four, one unit paid annually
  // in advance — because a portfolio where every cheque has cleared is not
  // what a landlord recognises.
  'demo-unit-creek-vistas': {
    annualRent: 105000,   // 8,750 / month
    expenses: 14800,
    collected: 52500,
    tenant: 'R. Okonkwo',
    status: 'Rented',
  },
  'demo-unit-wynwood': {
    annualRent: 94800,    // 7,900 / month
    expenses: 12100,
    collected: 47400,
    tenant: 'S. Petrova',
    status: 'Rented',
  },
  'demo-unit-one-park-central': {
    annualRent: 79200,    // 6,600 / month
    expenses: 10650,
    collected: 79200,     // paid for the year in one cheque
    tenant: 'A. Fernandes',
    status: 'Rented',
  },
  'demo-unit-address-residences': {
    annualRent: 348000,   // 29,000 / month
    expenses: 41000,
    collected: 174000,
    tenant: 'Khoury Family Office',
    status: 'Rented',
  },
  // Damac Bay 2, Nautica Two and Clearpoint are off-plan — no lease, which is
  // why the report shows them as "not yet earning rent" rather than as a gap
  // in the data. The two sold units have no lease either; their return is
  // realised, and the Sold tab reports it.
};

// ============================================================
// THE REST OF THE PORTFOLIO, UNDER MANAGEMENT
//
// The two hand-written units above carry the detail worth reading. The other
// six held units are BUILT from the portfolio itself, because a Property
// Management tab showing two properties while the portfolio shows ten reads
// as a broken screen — an owner counts them.
//
// Rents, tenants and contract dates come from the same places the rest of the
// app reads: DEMO_PORTAL_UNITS for the unit, DEMO_LEASES for the tenancy. Edit
// a rent in one place and every screen moves together.
//
// Sold units are deliberately absent: nobody manages a unit they no longer own.
// ============================================================

const PM_TENANTS: Record<string, string> = {
  'demo-unit-creek-vistas': 'R. Okonkwo',
  'demo-unit-wynwood': 'S. Petrova',
  'demo-unit-one-park-central': 'A. Fernandes',
  'demo-unit-address-residences': 'Khoury Family Office',
};

/** Contracts are staggered across the year — a real portfolio does not renew on one day. */
const PM_CONTRACTS: Record<string, { start: string; end: string; ejari: string; since: string }> = {
  'demo-unit-creek-vistas': { start: '2026-03-01', end: '2027-02-28', ejari: 'EJ-2026-204518', since: '2022-01-10' },
  'demo-unit-wynwood': { start: '2026-05-15', end: '2027-05-14', ejari: 'EJ-2026-311907', since: '2023-03-02' },
  'demo-unit-one-park-central': { start: '2025-11-01', end: '2026-10-31', ejari: 'EJ-2025-887214', since: '2022-10-05' },
  'demo-unit-address-residences': { start: '2026-02-10', end: '2027-02-09', ejari: 'EJ-2026-190442', since: '2021-09-15' },
};

const iso = (d: Date) => d.toISOString().slice(0, 10);
const plusMonths = (from: string, n: number) => {
  const d = new Date(from);
  d.setMonth(d.getMonth() + n);
  return iso(d);
};

/** Four quarterly cheques from the contract start; past ones have cleared. */
function pmSchedule(start: string, annualRent: number) {
  const each = Math.round(annualRent / 4);
  const today = new Date().setHours(0, 0, 0, 0);
  const dates = [0, 3, 6, 9].map((m) => plusMonths(start, m));
  const firstUnpaid = dates.findIndex((d) => new Date(d).getTime() >= today);
  return dates.map((dueDate, i) => ({
    chequeNo: `CHQ-${2100 + i}`,
    amount: each,
    dueDate,
    status: new Date(dueDate).getTime() < today
      ? 'paid'
      : i === firstUnpaid ? 'awaiting_approval' : 'scheduled',
  }));
}

function buildPmUnit(unitId: string) {
  const u = DEMO_PORTAL_UNITS.find((x) => x.id === unitId);
  const lease = DEMO_LEASES[unitId];
  const contract = PM_CONTRACTS[unitId];
  const sqft = Number(u.area);
  const annualRent = lease ? lease.annualRent : 0;
  const schedule = pmSchedule(contract.start, annualRent);
  const collected = schedule.filter((c) => c.status === 'paid').reduce((s, c) => s + c.amount, 0);
  const serviceChargePsf = Math.round((sqft > 1500 ? 21.5 : sqft > 1000 ? 14.8 : 13.2) * 10) / 10;
  const serviceChargeAnnual = Math.round(serviceChargePsf * sqft);

  return {
    id: u.id,
    name: u.name,
    unitNumber: u.unitNumber,
    community: u.project,
    location: `${u.project}, Dubai`,
    propertyType: 'Apartment',
    bedrooms: u.bedrooms,
    sizeSqft: sqft,
    areaSqm: Math.round(sqft * 0.0929 * 10) / 10,
    purchasePrice: u.purchaseValue,
    status: 'Rented',
    usage: 'Residential',
    propertyNo: `P-${u.unitNumber}-${u.name.split(' ').map((w: string) => w[0]).join('').toUpperCase()}`,
    plotNo: String(3400000 + sqft),
    dewaPremisesNo: String(321000000 + Math.round(sqft) * 37 + u.bedrooms),
    makaniNo: String(2688000000 + Math.round(sqft) * 211),
    image: '',
    projectImage: '',

    currentTenant: {
      name: PM_TENANTS[unitId],
      // Derived from the unit so every tenancy carries a plausible, distinct
      // set — same formats as the hand-written one above.
      emiratesId: `784-19${70 + (sqft % 25)}-${String(4000000 + Math.round(sqft) * 913).slice(0, 7)}-${sqft % 9}`,
      email: `${PM_TENANTS[unitId].split(' ').pop()!.toLowerCase().replace(/[^a-z]/g, '')}@example.com`,
      phone: `+971 5${u.bedrooms} ${String(100 + (Math.round(sqft) % 800))} ${String(1000 + (Math.round(sqft) % 8000)).slice(0, 4)}`,
      annualRent,
      contractStart: contract.start,
      contractEnd: contract.end,
      ejariNo: contract.ejari,
      occupants: Math.max(1, u.bedrooms),
    },

    rentSchedule: schedule,

    expenses: [
      { id: `${unitId}-exp-1`, category: 'Service charge', description: 'Service charge — H1', amount: Math.round(serviceChargeAnnual / 2), date: plusMonths(contract.start, 1) },
      { id: `${unitId}-exp-2`, category: 'Management fee', description: 'Management fee (5% of collected rent)', amount: Math.round(collected * 0.05), date: plusMonths(contract.start, 3) },
    ],

    maintenance: [
      { id: `${unitId}-mt-1`, title: 'Annual AC service', category: 'AC / Cooling', status: 'completed', createdAt: plusMonths(contract.start, 2), completedAt: plusMonths(contract.start, 2), cost: 420 },
    ],

    inspectionReports: [
      { id: `${unitId}-ins-1`, type: 'Move-in inspection', date: contract.start, inspector: 'Property team', findings: 'Unit handed over in good condition. Inventory list signed by tenant.', status: 'completed' },
    ],

    tenancyHistory: [
      { tenant: PM_TENANTS[unitId], from: contract.start, to: contract.end, annualRent, status: 'current' },
    ],

    lifecycle: [
      { step: 'Onboarded', date: contract.since, completed: true },
      { step: 'Marketed', date: plusMonths(contract.start, -2), completed: true },
      { step: 'Tenant found', date: plusMonths(contract.start, -1), completed: true },
      { step: 'Ejari registered', date: plusMonths(contract.start, 0), completed: true },
      { step: 'Renewal due', date: plusMonths(contract.end, -3), completed: false },
    ],

    facts: {
      serviceCharge: { psf: serviceChargePsf, annual: serviceChargeAnnual, source: 'invoice', sqft, bookedAsExpense: true, payableYet: true },
      construction: { offplan: false, percent: 100, percentSource: 'dld_project_registry', status: 'finished', handover: u.completionDate, handoverSource: 'unit', developer: u.constructionDeveloper },
      expectedRent: null,
      mortgage: { status: 'no', lender: null, outstanding: 0, monthlyPayment: 0, updatedAt: null },
      letting: { mode: 'long_term', brand: null },
      photo: null,
      netIncome: {
        annualRent,
        expenses: lease ? lease.expenses : 0,
        serviceCharge: 0,
        net: annualRent - (lease ? lease.expenses : 0),
        serviceChargeKnown: true,
      },
    },

    documents: [
      { id: `${unitId}-doc-1`, name: 'Tenancy contract.pdf', type: 'Tenancy Contract', date: contract.start },
      { id: `${unitId}-doc-2`, name: 'Ejari certificate.pdf', type: 'Ejari Certificate', date: plusMonths(contract.start, 0) },
    ],
  };
}

/** The two off-plan units that are not built yet — the same "not under management" state Damac Bay shows. */
function buildOffplanPmUnit(unitId: string) {
  const u = DEMO_PORTAL_UNITS.find((x) => x.id === unitId);
  const sqft = Number(u.area);
  return {
    id: u.id,
    name: u.name,
    unitNumber: u.unitNumber,
    community: u.project,
    location: `${u.project}, Dubai`,
    propertyType: 'Apartment',
    bedrooms: u.bedrooms,
    sizeSqft: sqft,
    areaSqm: Math.round(sqft * 0.0929 * 10) / 10,
    purchasePrice: u.purchaseValue,
    status: 'Off-Plan',
    usage: 'Residential',
    propertyNo: `P-${u.unitNumber}`,
    plotNo: String(3400000 + sqft),
    dewaPremisesNo: null,
    image: '',
    projectImage: '',
    currentTenant: null,
    rentSchedule: [],
    expenses: [],
    maintenance: [],
    inspectionReports: [],
    tenancyHistory: [],
    lifecycle: [
      { step: 'Onboarded', date: u.purchaseDate, completed: true },
      { step: 'Construction tracking', date: u.constructionStartDate, completed: true },
      { step: 'Handover expected', date: u.estimatedCompletionDate, completed: false },
      { step: 'Snagging', date: null, completed: false },
      { step: 'Tenant placement', date: null, completed: false },
    ],
    facts: {
      serviceCharge: { psf: null, annual: null, source: null, sqft, bookedAsExpense: false, payableYet: false },
      construction: {
        offplan: true, percent: u.constructionCompletionPercent, percentSource: 'dld_project_registry',
        status: 'active', handover: u.estimatedCompletionDate, handoverSource: 'unit', developer: u.constructionDeveloper,
      },
      expectedRent: null,
      mortgage: { status: 'no', lender: null, outstanding: 0, monthlyPayment: 0, updatedAt: null },
      letting: { mode: null, brand: null },
      photo: null,
      netIncome: null,
    },
    documents: [],
  };
}

DEMO_PM_UNITS.push(
  ...Object.keys(PM_TENANTS).map(buildPmUnit),
  ...['demo-unit-nautica', 'demo-unit-clearpoint', 'demo-unit-sobha-a5003', 'demo-unit-sobha-d1705'].map(buildOffplanPmUnit),
);
